// JSON-LD generators for schema.org structured data. Drop the output of any
// of these into a <SEO schemas={[...]} /> prop. Search engines (Google, Bing)
// AND AI engines (ChatGPT, Perplexity, Claude) consume this to understand
// who you are, what you sell, and where you operate.

const BASE = "https://propix3ls.com";
const LOGO = `${BASE}/logo.png`;
const SAME_AS = [
  "https://www.instagram.com/propix3ls",
  "https://www.tiktok.com/@propix3ls",
  "https://twitter.com/propix3ls",
];

export const organizationSchema = () => ({
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "Propix3ls",
  "alternateName": ["Pro Pix3ls", "ProPixels"],
  "url": BASE,
  "logo": LOGO,
  "description":
    "Lifestyle content brand offering same-day photo shoots, UGC content production, and 4K/8K reels for content creators, influencers, and TikTokers. Operating across the UK, USA and Canada.",
  "sameAs": SAME_AS,
  "areaServed": [
    { "@type": "Country", "name": "United Kingdom" },
    { "@type": "Country", "name": "United States" },
    { "@type": "Country", "name": "Canada" },
  ],
});

export const localBusinessSchema = ({
  region = "London", country = "UK", areaName = "London, UK",
  latitude, longitude, telephone = "",
} = {}) => ({
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "@id": `${BASE}/#${region.toLowerCase().replace(/\s+/g, "-")}`,
  "name": `Propix3ls — ${region}`,
  "image": LOGO,
  "url": BASE,
  "telephone": telephone,
  "priceRange": "$$-$$$",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": region,
    "addressCountry": country,
  },
  ...(latitude && longitude ? {
    "geo": { "@type": "GeoCoordinates", "latitude": latitude, "longitude": longitude },
  } : {}),
  "areaServed": areaName,
  "sameAs": SAME_AS,
  "openingHoursSpecification": [{
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
    "opens": "09:00",
    "closes": "21:00",
  }],
});

export const serviceSchema = ({ name, description, areaServed = ["United Kingdom", "United States", "Canada"], priceFrom, currency = "GBP" }) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": name,
  "name": name,
  "description": description,
  "provider": { "@type": "Organization", "name": "Propix3ls", "url": BASE },
  "areaServed": areaServed.map((c) => ({ "@type": "Country", "name": c })),
  ...(priceFrom ? {
    "offers": {
      "@type": "Offer",
      "priceCurrency": currency,
      "price": String(priceFrom),
      "availability": "https://schema.org/InStock",
      "url": `${BASE}/booking`,
    },
  } : {}),
});

export const bookSchema = ({ id, title, description, author = "Propix3ls", price, currency = "GBP", isFree = false, image, datePublished }) => ({
  "@context": "https://schema.org",
  "@type": "Book",
  "@id": `${BASE}/ebook/${id}`,
  "name": title,
  "description": description,
  "author": { "@type": "Organization", "name": author },
  "publisher": { "@type": "Organization", "name": "Propix3ls", "logo": { "@type": "ImageObject", "url": LOGO } },
  "url": `${BASE}/ebook/${id}`,
  ...(image ? { "image": image } : {}),
  ...(datePublished ? { "datePublished": datePublished } : {}),
  "inLanguage": "en",
  "bookFormat": "https://schema.org/EBook",
  "offers": {
    "@type": "Offer",
    "price": String(isFree ? 0 : price || 0),
    "priceCurrency": currency,
    "availability": "https://schema.org/InStock",
    "url": `${BASE}/ebook/${id}`,
  },
});

export const articleSchema = ({ slug, title, description, image, author = "Propix3ls", datePublished, dateModified }) => ({
  "@context": "https://schema.org",
  "@type": "BlogPosting",
  "mainEntityOfPage": { "@type": "WebPage", "@id": `${BASE}/blog/${slug}` },
  "headline": title,
  "description": description,
  "image": image ? [image] : [LOGO],
  "datePublished": datePublished,
  "dateModified": dateModified || datePublished,
  "author": { "@type": "Organization", "name": author, "url": BASE },
  "publisher": {
    "@type": "Organization",
    "name": "Propix3ls",
    "logo": { "@type": "ImageObject", "url": LOGO },
  },
});

export const faqSchema = (faqs = []) => ({
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": faqs.map(({ q, a }) => ({
    "@type": "Question",
    "name": q,
    "acceptedAnswer": { "@type": "Answer", "text": a },
  })),
});

export const breadcrumbSchema = (crumbs = []) => ({
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": crumbs.map((c, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "name": c.name,
    "item": c.url,
  })),
});

export const personSchema = ({ name, jobTitle = "Visual Content Creator", image }) => ({
  "@context": "https://schema.org",
  "@type": "Person",
  "name": name,
  "jobTitle": jobTitle,
  "url": BASE,
  ...(image ? { "image": image } : {}),
  "worksFor": { "@type": "Organization", "name": "Propix3ls" },
  "sameAs": SAME_AS,
});

export const websiteSchema = () => ({
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "Propix3ls",
  "url": BASE,
  "potentialAction": {
    "@type": "SearchAction",
    "target": { "@type": "EntryPoint", "urlTemplate": `${BASE}/blog?q={search_term_string}` },
    "query-input": "required name=search_term_string",
  },
});
